import React from 'react';
import styled, { css } from 'styled-components';
// import Information from './Information';
import { colors } from '../../styles/colors';
import FLIGHT_STATUS from '../../consts/FLIGHT_STATUS';
import FlightSchedule from './FlightSchedule';
import Icon from '../Icon';
import Moment from 'react-moment';
import 'moment/locale/pt-br';
import { devices } from '../../styles/breakpoints';
import { MediaQuery } from '../../components/MediaQuery';

const Container = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  border: 1px solid ${colors.GRAY_LIGHT};
  border-radius: 3px;

  @media ${devices.desktop} {
    max-width: 970px;
  }
`;

const typeMixin = css`
  font-size: 10px;
  font-weight: bold;
  padding: 4px 8px 12px 8px;
  ${({ type, isFlightStatus }) => {
    switch (type) {
      case FLIGHT_STATUS.CANCELLED:
        return `
          background-color: ${colors.GRAY_LIGHT};
          color: ${isFlightStatus ? colors.DANGER : colors.GRAY_DARKEST};

          @media ${devices.desktop} {
            color: ${colors.GRAY_DARKEST};
          }
        `;
      case FLIGHT_STATUS.SUGGESTED:
        return `
          background-color: ${colors.BRAND_PRIMARY};
          color: white;
        `;
      case FLIGHT_STATUS.CONFIRMED:
      default:
        return `
          background-color: ${colors.SUCCESS};
          color: white;
        `;
    }
  }}

  @media ${devices.desktop} {
    font-size: 16px;
  }
`;

const ColumnTitle = styled.span`
  ${typeMixin}
  ${({ center }) => (center ? `text-align: center;` : '')}
  max-height: 16px;

  ${({ isFlightStatus }) => isFlightStatus && `border-top-left-radius: 3px;`}

  @media ${devices.desktop} {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px;
    height: 41px;
    max-height: none;
  }
`;

const Information = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;
const Departure = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;
const Arrival = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;
const ArrowIconContainer = styled.div`
  display: flex;
  flex: 0;
  flex-direction: column;
`;
const Status = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
`;

const FlightName = styled.div`
  font-size: 13px;
  ${({ type }) =>
    type === FLIGHT_STATUS.SUGGESTED ? 'font-weight: bold;' : ''}
  color: ${({ type }) =>
    type === FLIGHT_STATUS.SUGGESTED
      ? colors.BRAND_SECONDARY
      : colors.GRAY_DARKER};


    @media ${devices.desktop} {
      font-size: 17.75px;
      text-align: center;
    }
`;

const AirplaneName = styled.div`
  font-size: 12px;
  color: ${({ type }) =>
    type === FLIGHT_STATUS.SUGGESTED
      ? colors.BRAND_SECONDARY
      : colors.GRAY_DARKER};
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  padding: 16px 6px;
  margin-top: 6px;
  margin-bottom: 6px;
  border-right: 1px solid ${colors.GRAY};
  white-space: nowrap;

  @media ${devices.desktop} {
    justify-content: center;
  }
`;

const ArrowIcon = styled(Icon)`
  font-size: 16px;
  color: ${({ type }) =>
    type === FLIGHT_STATUS.SUGGESTED
      ? colors.BRAND_SECONDARY
      : colors.GRAY_DARK};
`;

const GhostTitle = styled.div`
  height: 16px;
  ${typeMixin}

  @media ${devices.desktop} {
    height: 41px;
  }
`;

const GhostWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const StatusHeader = styled.div`
  height: 16px;
  border-top-right-radius: 3px;
  
  ${({ center }) => (center ? `text-align: center;` : '')}
  ${typeMixin}

  @media ${devices.desktop} {
    display: flex;
    align-items: center;
    height: 41px;
    padding: 0 8px;

    ${({ center }) => (center ? `justify-content: center;` : '')}
  }
`;

const StatusLabel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  margin: 6px;
  padding: 16px 6px;
  border-left: 1px solid ${colors.GRAY};
  color: ${colors.SUCCESS};
  font-size: 10px;
  font-weight: bold;
  ${({ center }) => (center ? `text-align: center;` : '')}

  ${({ type }) => {
    switch (type) {
      case FLIGHT_STATUS.CANCELLED:
        return `
          color: ${colors.DANGER};
        `;
      case FLIGHT_STATUS.SUGGESTED:
        return `
          color: ${colors.BRAND_PRIMARY};
        `;
      case FLIGHT_STATUS.CONFIRMED:
      default:
        return `
          color: ${colors.SUCCESS};
        `;
    }
  }}

  @media ${devices.desktop} {
    ${({ center }) => (center ? `justify-content: center;` : '')}
    font-size: 16px;
  }
  
`;

export default function FlightSummary({ flight, type }) {
  const _getFlightLabel = isFlightStatus => {
    switch (type) {
      case FLIGHT_STATUS.CANCELLED:
        return isFlightStatus ? 'Cancelado' : 'Voo cancelado';
      case FLIGHT_STATUS.SUGGESTED:
        return isFlightStatus ? 'Sugestão' : 'Novo voo sugerido';
      case FLIGHT_STATUS.CONFIRMED:
        return isFlightStatus ? 'Confirmado' : 'Voo confirmado';
      default:
        return 'Novo voo';
    }
  };
  return (
    <Container>
      <Information>
        <ColumnTitle type={type} isFlightStatus>
          {_getFlightLabel()}
        </ColumnTitle>
        <Content>
          <FlightName type={type}>
            Voo {flight.carrierCode} {flight.flightNumber}
          </FlightName>
          <AirplaneName type={type}>{flight.airplaneName}</AirplaneName>
        </Content>
      </Information>
      <Departure>
        <ColumnTitle type={type} center>
          Saída
        </ColumnTitle>
        <FlightSchedule
          flightTime={<Moment format="HH:mm">{flight.std}</Moment>}
          airportName={flight.departureStation}
          flightDate={<Moment format="DD/MM/YYYY">{flight.std}</Moment>}
          suggested={type === FLIGHT_STATUS.SUGGESTED}
        />
      </Departure>
      <ArrowIconContainer>
        <GhostTitle type={type} />
        <GhostWrapper>
          <ArrowIcon type={type} name="keyboard-arrow-right" />
        </GhostWrapper>
      </ArrowIconContainer>
      <Arrival>
        <ColumnTitle type={type} center>
          Chegada
        </ColumnTitle>
        <FlightSchedule
          flightTime={
            <Moment format="HH:mm">{flight.staFinalDestination}</Moment>
          }
          airportName={flight.arrivalStationFinalDestination}
          flightDate={
            <Moment format="DD/MM/YYYY">{flight.staFinalDestination}</Moment>
          }
          suggested={type === FLIGHT_STATUS.SUGGESTED}
          type={type}
        />
      </Arrival>

      <MediaQuery device={devices.desktop} style={{ flex: 1 }}>
        <Status>
          <StatusHeader type={type} center>
            Status
          </StatusHeader>

          <StatusLabel type={type} center>
            {_getFlightLabel(true)}
          </StatusLabel>
        </Status>
      </MediaQuery>
    </Container>
  );
}
